import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import {
  SkuCell,
  TransactionLineTable,
  type LineColumn,
} from '../components/TransactionLineTable.tsx';
import { apiFetch } from '../lib/api.ts';
import { dateOnly, num, statusClass, statusLabel } from '../lib/format.ts';

interface Line {
  lineNo: number;
  productCode: string | null;
  description: string | null;
  qty: number;
  location: string | null;
}

interface Detail {
  id: number;
  doNo: string;
  doDate: string;
  sourceSoNo: string | null;
  customerNo: string | null;
  customerName: string | null;
  salesman: string | null;
  shipAddr1: string | null;
  shipAddr2: string | null;
  shipAddr3: string | null;
  shipAddr4: string | null;
  shipAddr5: string | null;
  status: string;
  lines: Line[];
}

export function DeliveryOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['delivery-order', id],
    queryFn: () =>
      apiFetch<Detail>(`/api/dashboard/delivery-orders/${encodeURIComponent(id ?? '')}`),
    enabled: !!id,
  });

  const productLines = (data?.lines ?? []).filter((l) => l.productCode);
  const columns: LineColumn<Line>[] = [
    { label: '#', className: 'mono', render: (l) => l.lineNo },
    { label: 'SKU', className: 'mono', render: (l) => <SkuCell code={l.productCode} /> },
    { label: 'Description', render: (l) => l.description || '—' },
    { label: 'Loc', className: 'mono', render: (l) => l.location || '—' },
    { label: 'Qty', className: 'num', render: (l) => num(l.qty) },
  ];

  const shipAddr = data
    ? [data.shipAddr1, data.shipAddr2, data.shipAddr3, data.shipAddr4, data.shipAddr5].filter(
        (s) => s && s.trim(),
      )
    : [];

  return (
    <>
      <Link to="/delivery-orders" className="back-link">← All delivery orders</Link>
      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}
      {data && (
        <>
          <header className="main-header">
            <div>
              <h1 className="main-title">DO {data.doNo}</h1>
              <span className="main-subtitle mono">{dateOnly(data.doDate)}</span>
            </div>
            <span className={`badge ${statusClass(data.status)}`}>{statusLabel(data.status)}</span>
          </header>
          <section className="panel">
            <header className="panel-header"><h2 className="panel-title">Header</h2></header>
            <dl className="detail-grid">
              <dt>DO no</dt><dd className="mono">{data.doNo}</dd>
              <dt>Source SO</dt>
              <dd className="mono">
                {data.sourceSoNo ? (
                  <Link to={`/sales-orders/${encodeURIComponent(data.sourceSoNo)}`}>
                    {data.sourceSoNo}
                  </Link>
                ) : '—'}
              </dd>
              <dt>Date</dt><dd>{dateOnly(data.doDate)}</dd>
              <dt>Customer</dt>
              <dd>
                {data.customerNo ? (
                  <Link to={`/customers/${encodeURIComponent(data.customerNo)}`}>
                    {data.customerName ?? data.customerNo}
                  </Link>
                ) : '—'}
              </dd>
              <dt>Salesman</dt><dd>{data.salesman || '—'}</dd>
              {shipAddr.length > 0 && (
                <>
                  <dt>Ship to</dt>
                  <dd>
                    {shipAddr.map((s, i) => <div key={i}>{s}</div>)}
                  </dd>
                </>
              )}
            </dl>
          </section>
          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Line items</h2>
              <span className="panel-subtitle">{productLines.length} item{productLines.length === 1 ? '' : 's'}</span>
            </header>
            <TransactionLineTable lines={productLines} columns={columns} />
          </section>
        </>
      )}
    </>
  );
}
